import { z } from "zod";
import {
  JOURNEY_SUBJECT_TYPES,
  JOURNEY_SEND_CLASSES,
  JOURNEY_REENROLLMENTS,
  CONDITION_OPS,
} from "../types/journeys";

// ============================================================================
// JOURNEYS — Zod validation for the universal automation engine (B3 řez 2)
// ============================================================================
// Validated by the admin journeys route on create/update. Steps are capped at 25
// (an embedded JSON array — a runaway step list is a foot-gun, not a feature).

// ── Conditions (recursive group) ────────────────────────────────────────────

export const conditionLeafSchema = z.object({
  field: z.string().min(1),
  op: z.enum(CONDITION_OPS),
  value: z
    .union([z.string(), z.number(), z.boolean(), z.array(z.union([z.string(), z.number()]))])
    .optional(),
});

// z.lazy keeps the group recursive (a group may nest groups under all/any).
export const conditionGroupSchema: z.ZodType<{
  all?: Array<unknown>;
  any?: Array<unknown>;
}> = z.lazy(() =>
  z
    .object({
      all: z.array(z.union([conditionLeafSchema, conditionGroupSchema])).optional(),
      any: z.array(z.union([conditionLeafSchema, conditionGroupSchema])).optional(),
    })
    .refine((g) => g.all !== undefined || g.any !== undefined, {
      message: "Condition group needs an all[] or any[] branch",
    }),
);

// ── Triggers ────────────────────────────────────────────────────────────────

export const journeyTriggerSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("status_change"),
    from: z.array(z.string()).optional(),
    to: z.array(z.string()).min(1),
  }),
  z.object({
    type: z.literal("field_change"),
    field: z.string().min(1),
    toValue: z.union([z.string(), z.number()]).optional(),
    gte: z.number().optional(),
    lte: z.number().optional(),
  }),
  z.object({
    type: z.literal("time_offset"),
    dateField: z.string().min(1),
    daysOffset: z.number().int(),
    timeOfDay: z
      .string()
      .regex(/^\d{2}:\d{2}$/)
      .optional(),
    onlyIfStatus: z.array(z.string()).optional(),
  }),
  z.object({ type: z.literal("event"), eventType: z.string().min(1) }),
  z.object({
    type: z.literal("inactivity"),
    sinceField: z.string().min(1),
    days: z.number().int().positive(),
    onlyIfStatus: z.array(z.string()).optional(),
  }),
  z.object({
    type: z.literal("recurring"),
    everyDays: z.number().int().positive().optional(),
    cron: z.string().optional(),
  }),
  z.object({ type: z.literal("manual") }),
]);

// ── Actions ─────────────────────────────────────────────────────────────────

export const journeyActionSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("send_email"),
    recipient: z.enum(["customer", "assignee", "admin", "email_field"]),
    emailField: z.string().optional(),
    templateSlug: z.string().optional(),
    subject: z.string().optional(),
    body: z.string().optional(),
  }),
  z.object({
    type: z.literal("send_sms"),
    recipient: z.enum(["customer", "assignee", "phone_field"]),
    phoneField: z.string().optional(),
    templateSlug: z.string().optional(),
    body: z.string().optional(),
  }),
  z.object({
    type: z.literal("send_whatsapp"),
    recipient: z.enum(["customer", "assignee", "phone_field"]),
    phoneField: z.string().optional(),
    templateSlug: z.string().optional(),
    body: z.string().optional(),
  }),
  z.object({
    type: z.literal("create_task"),
    kind: z.enum(["callback", "manual", "supplier_followup"]).optional(),
    title: z.string().min(1),
    description: z.string().optional(),
    dueInDays: z.number().int().optional(),
    assignTo: z.enum(["subject_assignee", "user"]).optional(),
    userId: z.string().optional(),
  }),
  z.object({
    type: z.literal("assign"),
    mode: z.enum(["round_robin", "user"]),
    userId: z.string().optional(),
  }),
  z.object({
    type: z.literal("notify_user"),
    target: z.enum(["subject_assignee", "user"]),
    userId: z.string().optional(),
    message: z.string().min(1),
  }),
  z.object({ type: z.literal("add_to_segment"), segmentId: z.string().min(1) }),
  z.object({ type: z.literal("remove_from_segment"), segmentId: z.string().min(1) }),
  z.object({
    type: z.literal("generate_document"),
    docType: z.enum(["contract", "itinerary", "voucher", "package"]),
  }),
  z.object({
    type: z.literal("webhook"),
    url: z.string().url(),
    method: z.enum(["POST", "GET"]).optional(),
    bodyTemplate: z.string().optional(),
  }),
  z.object({
    type: z.literal("agent_action"),
    instruction: z.string().min(1).max(2000),
    allowMoney: z.boolean().optional(),
  }),
]);

// ── Step ──────────────────────────────────────────────────────────────────────

export const journeyStepSchema = z.object({
  delay: z
    .object({
      days: z.number().int().nonnegative().optional(),
      hours: z.number().int().nonnegative().optional(),
      atTimeOfDay: z
        .string()
        .regex(/^\d{2}:\d{2}$/)
        .optional(),
    })
    .optional(),
  conditions: conditionGroupSchema.optional(),
  ifConditionsFail: z.enum(["skip_step", "exit_journey"]).optional(),
  actions: z.array(journeyActionSchema).min(1),
});

// ── Journey definition ──────────────────────────────────────────────────────

export const journeyCreateSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(2000).optional().nullable(),
  enabled: z.boolean().optional(),
  subjectType: z.enum(JOURNEY_SUBJECT_TYPES),
  trigger: journeyTriggerSchema,
  entryConditions: conditionGroupSchema.optional().nullable(),
  steps: z.array(journeyStepSchema).min(1).max(25),
  reenrollment: z.enum(JOURNEY_REENROLLMENTS).optional(),
  maxConcurrentPerSubject: z.number().int().positive().max(100).optional(),
  sendClass: z.enum(JOURNEY_SEND_CLASSES).optional(),
  priority: z.number().int().optional(),
});

export const journeyUpdateSchema = journeyCreateSchema.partial();

export type JourneyCreateInput = z.infer<typeof journeyCreateSchema>;
export type JourneyUpdateInput = z.infer<typeof journeyUpdateSchema>;
